/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useTranslation } from "react-i18next";

import styles from "./ChatWidget.module.css";
import { logger } from "matrix-js-sdk/lib/logger";
import { useState, useEffect, useRef, type CSSProperties } from "react";
import {
  useDataChannel,
  useLocalParticipant,
  useParticipants,
} from "@livekit/components-react";

import { createPortal, flushSync } from "react-dom";
import { useMediaQuery } from "../../useMediaQuery";
import { useChatViewport } from "./useChatViewport";

const CHAT_TOPIC = "ephemeral-chat";

interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  timestamp: number;
}

type ChatPayload =
  | { type: "msg"; message: ChatMessage }
  | { type: "sync"; messages: ChatMessage[] };

export const ChatWidget = ({
  displayName,
  toggleTarget,
}: {
  displayName?: string;
  toggleTarget?: HTMLElement | null;
}) => {
  const { t } = useTranslation();
  const viewport = useChatViewport();
  const mobile = useMediaQuery(
    "(hover: none), (pointer: coarse), (max-width: 600px), (max-height: 500px)",
  );
  const touchInput = useMediaQuery("(hover: none), (pointer: coarse)");
  const docked = mobile && Boolean(toggleTarget);
  const [isOpen, setIsOpen] = useState(false);
  const [closingViewport, setClosingViewport] = useState<typeof viewport | null>(null);
  const panelViewport = isOpen ? viewport : closingViewport ?? viewport;
  const [unreadCount, setUnreadCount] = useState(0);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState("");

  const { send, message } = useDataChannel(CHAT_TOPIC);
  const { localParticipant } = useLocalParticipant();
  const participants = useParticipants();

  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [chatWidth, setChatWidth] = useState(350);
  const resizeStart = useRef<{ x: number; width: number } | null>(null);
  const closeChat = (): void => {
    // Freeze geometry during exit; keyboard dismissal must not stretch the panel.
    setClosingViewport(viewport);
    inputRef.current?.blur();
    setIsOpen(false);
  };
  const toggleChat = (): void => {
    if (isOpen) {
      closeChat();
    } else {
      setClosingViewport(null);
      // Focus immediately on desktop; touch users open the keyboard by tapping
      // the composer themselves, so opening chat does not hide the conversation.
      flushSync(() => setIsOpen(true));
      if (!touchInput) inputRef.current?.focus({ preventScroll: true });
    }
  };

  // --- НАШ ПАТЧ: ЗАЩИТА ОТ ФАНТОМНЫХ СООБЩЕНИЙ ---
  const lastProcessedMsgId = useRef<string | null>(null);

  const myId = localParticipant.identity || "unknown";
  const myName = displayName || localParticipant.name || t("instance.ui.guest");

  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (scrollRef.current && isOpen) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  // 1. Прием сообщений
  useEffect(() => {
    if (message && message.topic === CHAT_TOPIC) {
      const decoder = new TextDecoder();
      try {
        const data: ChatPayload = JSON.parse(decoder.decode(message.payload));

        if (data.type === "msg") {
          // ЕСЛИ МЫ УЖЕ СЧИТАЛИ ЭТО СООБЩЕНИЕ - ИГНОРИРУЕМ
          if (lastProcessedMsgId.current === data.message.id) return;
          lastProcessedMsgId.current = data.message.id;

          setMessages((prev) => {
            if (prev.find((m) => m.id === data.message.id)) return prev;
            return [...prev, data.message].sort(
              (a, b) => a.timestamp - b.timestamp,
            );
          });
          if (!isOpen) setUnreadCount((c) => c + 1);
        } else if (data.type === "sync") {
          setMessages((prev) => {
            const merged = [...prev];
            let changed = false;
            data.messages.forEach((msg) => {
              if (!merged.find((m) => m.id === msg.id)) {
                merged.push(msg);
                changed = true;
              }
            });
            return changed
              ? merged.sort((a, b) => a.timestamp - b.timestamp)
              : prev;
          });
        }
      } catch {
        logger.warn("Invalid call chat message");
      }
    }
  }, [message, isOpen]);

  // 2. Раздача истории новичкам
  const prevParticipantsCount = useRef(participants.length);
  useEffect(() => {
    if (
      participants.length > prevParticipantsCount.current &&
      messagesRef.current.length > 0
    ) {
      const encoder = new TextEncoder();
      const payload = encoder.encode(
        JSON.stringify({ type: "sync", messages: messagesRef.current }),
      );
      send(payload, { reliable: true }).catch(() =>
        logger.warn("Call chat sync failed"),
      );
    }
    prevParticipantsCount.current = participants.length;
  }, [participants.length, send]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim()) return;
    if (document.activeElement !== inputRef.current)
      inputRef.current?.focus({ preventScroll: true });

    const newMsg: ChatMessage = {
      id: Math.random().toString(36).substring(2, 10),
      senderId: myId,
      senderName: myName,
      text: inputText.trim(),
      timestamp: Date.now(),
    };

    const encoder = new TextEncoder();
    const payload = encoder.encode(
      JSON.stringify({ type: "msg", message: newMsg }),
    );

    await send(payload, { reliable: true });

    setMessages((prev) => [...prev, newMsg]);
    setInputText("");
  };

  const toggle = (
    <button
      className={`${styles.toggle} ${docked ? styles.dockedToggle : ""}`}
      aria-label={t("instance.ui.open_chat")}
      onClick={toggleChat}
      style={{
        position: "absolute",
        bottom: "100px",
        right: "20px",
        zIndex: 9999,
        width: "56px",
        height: "56px",
        borderRadius: "50%",
        color: "var(--app-text)",
        backgroundColor: "var(--app-chrome)",
        border: "1px solid var(--app-border)",
        cursor: "pointer",
        boxShadow: "0 4px 16px var(--glass-shadow)",
        display: isOpen ? "none" : "flex",
        justifyContent: "center",
        alignItems: "center",
        transition: "transform 0.2s, background 0.2s",
        backdropFilter: "blur(8px)",
      }}
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M21 11.5C21 16.75 16.5 21 12 21C10.5 21 9 20.5 7.5 19.5L3 21L4.5 16.5C3.5 15 3 13.5 3 11.5C3 6.25 7.5 2 12 2C16.5 2 21 6.25 21 11.5Z" />
      </svg>
      {unreadCount > 0 && (
        <span
          style={{
            position: "absolute",
            top: "0",
            right: "0",
            backgroundColor: "#ff3b30",
            color: "var(--app-text)",
            fontSize: "12px",
            fontWeight: "bold",
            height: "20px",
            minWidth: "20px",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 6px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
          }}
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </button>
  );
  return createPortal(
    <div
      className={styles.layer}
      data-call-chat
      style={
        {
          "--chat-viewport-height": `${panelViewport.height}px`,
          "--chat-viewport-top": `${panelViewport.top}px`,
        } as CSSProperties
      }
    >
      {docked ? createPortal(toggle, toggleTarget!) : toggle}

      <div
        className={styles.panel}
        data-open={isOpen}
        aria-hidden={!isOpen}
        inert={!isOpen}
        data-compact={panelViewport.height < 260}
        data-tiny={panelViewport.height < 160}
        data-keyboard-open={
          panelViewport.height <
          Math.max(window.innerHeight, document.documentElement.clientHeight) -
            100
        }
        role="region"
        aria-label={t("instance.ui.call_chat")}
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          transform: isOpen ? "translateX(0)" : "translateX(110%)",
          bottom: "80px",
          width: `min(${chatWidth}px, 100%)`,
          color: "var(--app-text)",
          backgroundColor: "var(--app-chrome)",
          backdropFilter: "blur(28px) saturate(140%)",
          WebkitBackdropFilter: "blur(28px) saturate(140%)",
          borderRadius: "24px 0 0 24px",
          overflow: "hidden",
          borderLeft: "1px solid var(--app-border)",
          zIndex: 9998,
          display: "flex",
          flexDirection: "column",
          boxShadow: isOpen ? "-8px 0 24px var(--glass-shadow)" : "none",
        }}
      >
        <div
          className={styles.resizeHandle}
          role="separator"
          aria-label={t("instance.ui.resize_chat")}
          aria-orientation="vertical"
          aria-valuemin={300}
          aria-valuemax={Math.max(300, Math.min(800, window.innerWidth - 48))}
          aria-valuenow={chatWidth}
          tabIndex={isOpen ? 0 : -1}
          onPointerDown={(event) => {
            if (event.pointerType !== "mouse") return;
            resizeStart.current = { x: event.clientX, width: chatWidth };
            event.currentTarget.setPointerCapture(event.pointerId);
            event.preventDefault();
          }}
          onPointerMove={(event) => {
            const start = resizeStart.current;
            if (!start) return;
            setChatWidth(
              Math.max(
                300,
                Math.min(
                  800,
                  window.innerWidth - 48,
                  start.width + start.x - event.clientX,
                ),
              ),
            );
          }}
          onPointerUp={() => {
            resizeStart.current = null;
          }}
          onLostPointerCapture={() => {
            resizeStart.current = null;
          }}
          onKeyDown={(event) => {
            if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
            event.preventDefault();
            setChatWidth((width) =>
              Math.max(
                300,
                Math.min(
                  800,
                  window.innerWidth - 48,
                  width + (event.key === "ArrowLeft" ? 24 : -24),
                ),
              ),
            );
          }}
        />
        <div
          className={styles.chatHeader}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px",
            flexShrink: 0,
            borderBottom: "1px solid var(--app-border)",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              color: "var(--app-text)",
              fontWeight: 600,
            }}
          >
            {t("instance.ui.call_chat")}
          </h3>
          <button
            className={styles.close}
            aria-label={t("instance.ui.close_chat")}
            onClick={closeChat}
            style={{
              background: "none",
              border: "none",
              color: "var(--app-muted)",
              cursor: "pointer",
              fontSize: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "4px",
              transition: "background 0.2s",
            }}
          >
            ×
          </button>
        </div>

        <div
          ref={scrollRef}
          style={{
            flexGrow: 1,
            minHeight: 0,
            overscrollBehavior: "contain",
            overflowY: "auto",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {messages.length === 0 ? (
            <div
              style={{
                color: "var(--app-muted)",
                margin: "auto",
                textAlign: "center",
                fontSize: "14px",
              }}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                style={{ marginBottom: "12px", opacity: 0.5 }}
              >
                <path d="M21 11.5C21 16.75 16.5 21 12 21C10.5 21 9 20.5 7.5 19.5L3 21L4.5 16.5C3.5 15 3 13.5 3 11.5C3 6.25 7.5 2 12 2C16.5 2 21 6.25 21 11.5Z" />
              </svg>
              <br />
              {t("instance.ui.chat_empty")}
              <br />
              {t("instance.ui.first_message")}
            </div>
          ) : (
            messages.map((m) => {
              const isMe = m.senderId === myId;
              const displayInfo = isMe ? t("instance.ui.me") : m.senderName;

              return (
                <div
                  key={m.id}
                  style={{
                    alignSelf: isMe ? "flex-end" : "flex-start",
                    maxWidth: "85%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: isMe ? "flex-end" : "flex-start",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      color: "var(--app-accent)",
                      marginBottom: "4px",
                      padding: "0 4px",
                      fontWeight: "bold",
                    }}
                  >
                    {displayInfo}
                  </span>
                  <div
                    className="chat-message"
                    onPointerUp={(event) => event.stopPropagation()}
                    style={{
                      whiteSpace: "pre-wrap",
                      background: isMe
                        ? "var(--glass-accent)"
                        : "var(--app-field)",
                      color: "var(--app-text)",
                      padding: "10px 14px",
                      borderRadius: isMe
                        ? "16px 16px 4px 16px"
                        : "16px 16px 16px 4px",
                      fontSize: "14px",
                      lineHeight: "1.4",
                      wordBreak: "break-word",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                  >
                    {m.text}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <form
          className={styles.composer}
          onSubmit={(event) =>
            void handleSend(event).catch(() =>
              logger.warn("Call chat send failed"),
            )
          }
          style={{
            padding: "16px",
            flexShrink: 0,
            borderTop: "1px solid var(--app-border)",
            display: "flex",
            gap: "8px",
            backgroundColor: "var(--glass-fill)",
          }}
        >
          <input
            ref={inputRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={t("instance.ui.message_placeholder")}
            style={{
              minWidth: 0,
              flex: 1,
              padding: "12px 16px",
              borderRadius: "24px",
              border: "1px solid var(--app-border)",
              background: "var(--app-field)",
              color: "var(--app-text)",
              outline: "none",
              fontSize: "16px",
              transition: "border 0.2s",
            }}
            onFocus={(e) =>
              (e.target.style.border = "1px solid var(--app-accent)")
            }
            onBlur={(e) =>
              (e.target.style.border = "1px solid var(--app-border)")
            }
          />
          <button
            aria-label={t("instance.ui.send_message")}
            type="submit"
            onPointerDown={(event) => {
              // Sending must not blur the composer and collapse the phone keyboard.
              event.preventDefault();
            }}
            disabled={!inputText.trim()}
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "50%",
              cursor: inputText.trim() ? "pointer" : "default",
              backgroundColor: "var(--glass-accent)",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "0.2s",
              color: "var(--app-accent)",
              opacity: inputText.trim() ? 1 : 0.45,
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </form>
      </div>
    </div>,
    document.body,
  );
};
